import { createContext, useContext, useMemo } from 'react';

import { defaultLanguage, supportedLanguages, type SupportedLanguage } from '@modernloop/i18n';
import { z } from 'zod';

import { useFeatureFlagJson } from './useFeatureFlagJson';

/**
 * Zod schema for validating the languages feature flag.
 * Validates that the value is an array of supported language codes.
 */
const languagesSchema = z.array(z.enum(supportedLanguages));

/**
 * Returns the FF universe of choosable locales — the full set an org admin may enable.
 * Used by the org admin "Add language" picker (EditLanguagesDialog), which filters this down
 * to backend-supported locales before persisting. For the org's currently-enabled locales,
 * use {@link useLocales} instead.
 *
 * This hook:
 * - Fetches the `languages` JSON feature flag
 * - Filters out any unexpected or invalid input
 * - Always returns an array with at least one value
 * - Always includes `en-US` (the default language) in the returned list
 * - Supports ["ALL"] as a special value to enable all languages (useful for development)
 *
 * @returns An array of supported locale codes. Never returns an empty array.
 */
export function useSelectableLocales(): SupportedLanguage[] {
  const languagesFlagValue = useFeatureFlagJson('languages');

  return useMemo((): SupportedLanguage[] => {
    // Default to just English if flag is falsy
    if (!languagesFlagValue) {
      return [defaultLanguage];
    }

    // Dev shortcut: ["ALL"] returns all supported languages
    if (Array.isArray(languagesFlagValue) && languagesFlagValue.includes('ALL')) {
      return [...supportedLanguages];
    }

    // Validate with Zod safeParse
    const result = languagesSchema.safeParse(languagesFlagValue);

    if (!result.success) {
      // Log validation error for debugging but don't crash
      console.warn('Invalid languages feature flag value:', result.error.message);
      return [defaultLanguage];
    }

    const validLocales = result.data;

    // If empty array, return default
    if (validLocales.length === 0) {
      return [defaultLanguage];
    }

    // Ensure default language is always included
    if (!validLocales.includes(defaultLanguage)) {
      return [defaultLanguage, ...validLocales];
    }

    return validLocales;
  }, [languagesFlagValue]);
}

/**
 * Context that carries the org-enabled locale list, injected at the app boundary by app-client.
 * Default is [defaultLanguage] so that callers outside a provider always get a safe value.
 */
export const SupportedLocalesContext = createContext<SupportedLanguage[]>([defaultLanguage]);

/**
 * Returns the org's enabled locales, injected by the app at the org boundary.
 * Falls back to [defaultLanguage] if the context is empty or not provided.
 *
 * @returns An array of supported locale codes. Never returns an empty array.
 *
 * @example
 * const locales = useLocales();
 * // Returns: ['en-US'] if org pref is not set
 * // Returns: ['en-US', 'ja-JP', 'pt-BR'] if org has those locales enabled
 */
export function useLocales(): SupportedLanguage[] {
  const locales = useContext(SupportedLocalesContext);
  return locales.length ? locales : [defaultLanguage];
}

/**
 * Whether any language-picker UI should be shown. Requires BOTH:
 * - the `languages` feature flag to grant more than English (the FF is the master on/off gate), and
 * - the org to have more than one locale enabled (nothing to pick otherwise).
 *
 * Gating on the FF here ensures pickers stay hidden when the flag is off, even if the org pref
 * was populated out-of-band.
 */
export function useShowLanguageSelector(): boolean {
  return useSelectableLocales().length > 1 && useLocales().length > 1;
}
